import React from 'react';
import { Inbox, CheckCircle, Tag, Star } from 'lucide-react';
import { Email } from './types';

interface MailListProps {
    loading: boolean;
    emailsLoading: boolean;
    selectedEmailAccount: string;
    filteredEmails: Email[];
    selectedEmail: Email | null;
    onSelectEmail: (email: Email) => void;
    searchTerm: string;
    formatDate: (date: string) => string;
}

const MailList: React.FC<MailListProps> = ({
    loading,
    emailsLoading,
    selectedEmailAccount,
    filteredEmails,
    selectedEmail,
    onSelectEmail,
    searchTerm,
    formatDate
}) => {
    if (loading) {
        return (
            <div className="w-1/2 bg-white border-r border-gray-200 overflow-y-auto">
                <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Email hesapları yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (emailsLoading) {
        return (
            <div className="w-1/2 bg-white border-r border-gray-200 overflow-y-auto">
                <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Mailler yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (!selectedEmailAccount) {
        return (
            <div className="w-1/2 bg-white border-r border-gray-200 overflow-y-auto">
                <div className="p-8 text-center">
                    <Inbox className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Email Hesabı Seçin</h3>
                    <p className="text-gray-500">
                        Mailleri görüntülemek için yukarıdan bir email hesabı seçin.
                    </p>
                </div>
            </div>
        );
    }

    if (filteredEmails.length === 0) {
        return (
            <div className="w-1/2 bg-white border-r border-gray-200 overflow-y-auto">
                <div className="p-8 text-center">
                    <Inbox className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Email Bulunamadı</h3>
                    <p className="text-gray-500 mb-4">
                        {searchTerm
                            ? 'Arama kriterlerinize uygun email bulunamadı.'
                            : 'Seçilen email hesabında henüz email bulunamadı.'
                        }
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-1/2 bg-white border-r border-gray-200 overflow-y-auto">
            <div className="divide-y divide-gray-200">
                {filteredEmails.map((email) => (
                    <div
                        key={email.id}
                        onClick={() => onSelectEmail(email)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedEmail?.id === email.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                            } ${!email.isRead ? 'bg-blue-25' : ''}`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                    <span className={`inline-block w-3 h-3 rounded-full ${email.categoryColor}`}></span>
                                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                        {email.category}
                                    </span>
                                    {email.isMatched && (
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    )}
                                    {!email.isRead && (
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    )}
                                </div>

                                <h3 className={`text-sm mb-1 ${email.isRead ? 'text-gray-700' : 'text-gray-900 font-medium'
                                    }`}>
                                    {email.subject}
                                </h3>

                                <p className="text-xs text-gray-500 mb-2">
                                    {email.sender}
                                </p>

                                <p className="text-sm text-gray-600 line-clamp-2">
                                    {email.content}
                                </p>

                                {email.matchedKeywords.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {email.matchedKeywords.map((keyword, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                                            >
                                                <Tag className="h-3 w-3 mr-1" />
                                                {keyword}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                                <button
                                    onClick={(e) => e.stopPropagation()}
                                    className={`p-1 rounded hover:bg-gray-100 ${email.isStarred ? 'text-yellow-500' : 'text-gray-400'
                                        }`}
                                >
                                    <Star className="h-4 w-4" />
                                </button>
                                <span className="text-xs text-gray-500">
                                    {formatDate(email.receivedAt)}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MailList;
